import { Button, Col, Divider, Dropdown, Input, Popconfirm, Row, Select, Switch } from 'antd';
import { PlusCircleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";

import FormLayout from '../../FormLayout/FormLayout';
import EmailField from '../../Fields/EmailField/EmailField';
import NameField from '../../Fields/NameField/NameField';
import LastNameField from '../../Fields/LastNameField/LastNameField';
import DocumentExpirationField from '../../Fields/DocumentExpirationField/DocumentExpirationField';
import CountryField from '../../Fields/CountryField/CountryField';
import FundsSourceField from '../../Fields/FundsSourceField/FundsSourceField';
import RoleField from '../../Fields/RoleField/RoleField';
import DateField from '../../Fields/DateField/DateField';
import TextField from '../../Fields/TextField/TextField';
import SecondLastNameField from '../../Fields/SecondLastNameField/SecondLastNameField';

import { InvestorAPI } from '../../../Api/InvestorAPI';
import { BeneficiaryAPI } from '../../../Api/BeneficiaryAPI';
import { ContractAPI } from '../../../Api/ContractAPI';
import { useBeneficiaries } from '../../../Hooks/useBeneficiaries/useBeneficiaries';

import { IBeneficiaries } from './IBeneficiaries';
import { Beneficiaries as BeneficiariesModel } from '../../../Models';
import { getPhysicalSourceOptions } from '../../../Assets/OptionsTranslations';

import './Beneficiaries.scss';
import DocumentTypeField from '../../Fields/DocumentTypeField/DocumentTypeField';

const Beneficiaries = ({
	baseURL,
	baseURLExternal,
	translations,
	clientId,
	unlockNextStep,
	language,
	contractLanguage,
	mode = 'full',
	isReadOnly = false
}: IBeneficiaries) => {
	const investorAPI = InvestorAPI(baseURL, baseURLExternal, clientId);
	const beneficiaryAPI = BeneficiaryAPI(baseURL, clientId);
	const contractAPI = ContractAPI(baseURL, baseURLExternal, clientId);

	const {
		placesService,
		placePredictions,
		getPlacePredictions,
		isPlacePredictionsLoading,
	} = usePlacesService({ apiKey: "AIzaSyCU9qZlMZtXtxKvRJi-NRNJtPJF0p7DpYo" });

	const {
		loading,
		beneficiaries,
		errors,
		nationalitiesList,
		contractId,
		taxAddressInputs,
		manualAddressVisibility,
		activeAddress,
		addRow,
		handleChangeData,
		handleSelectData,
		handleChangeDate,
		getInfo,
		deleteRow,
		saveData,
		handleSelectAddress,
		handleSearchAddress,
		onChangeHasPRP,
		onChangeIsBeneficialOwner,
		prefillOptions,
	} = useBeneficiaries({
		translations,
		investorAPI,
		beneficiaryAPI,
		contractAPI,
		placesService,
		unlockNextStep,
		getPlacePredictions,
		mode,
	});

	const isLiteMode = mode === 'lite';
	const beneficiariesLiteSubtitle = translations.beneficiariosMas5Subtitulo || translations.beneficiariesOver5Subtitle;
	const physicalSourceOptions = getPhysicalSourceOptions(language);
	const addBeneficiaryMenuItems = [
		{
			key: 'new',
			label: translations.addEmptyBeneficiary || translations.masBeneficiarios,
		},
		...prefillOptions.map((option) => ({
			key: `investor-${option.value}`,
			label: `${translations.prefillBeneficiaryFromInvestor || 'Firmante'}: ${option.label}`,
		}))
	];

	const handleAddBeneficiaryMenuClick = ({ key }: { key: string }) => {
		if (key === 'new') {
			addRow();
			return;
		}

		if (key.startsWith('investor-')) {
			addRow(Number(key.replace('investor-', '')));
		}
	};

	return (
		<FormLayout
			translations={ translations }
			loading={ loading }
			title={ isLiteMode ? translations.beneficiariosMas5 : translations.tituloBenerficiarios }
			subTitle={
				isLiteMode
					? beneficiariesLiteSubtitle
					: <>
						<Row><h3>{translations.beneficiariesHeader1 }</h3></Row>
						<br/>
						<Row>{translations.beneficiariesHeaderExplanation1}</Row>
						<br/>
						<Row><h3>{translations.beneficiariesHeader2 }</h3></Row>
						<br/>
						<Row>{translations.beneficiariesHeaderExplanation2}</Row>
					</>
			}
			onClick={ saveData }
			contractId={ contractId }
			nextStep={ unlockNextStep }
			enableNextStep
			forceEnableNextStep={ isReadOnly }
			hideSaveButton={ isReadOnly }
		>
			<Dropdown
				menu={{ items: addBeneficiaryMenuItems, onClick: handleAddBeneficiaryMenuClick }}
				disabled={isReadOnly}
				trigger={['click']}
			>
				<Button
					className="save-btn"
					disabled={ isReadOnly }
				>
					<PlusCircleOutlined />{translations.masBeneficiarios }
				</Button>
			</Dropdown>
			{beneficiaries.map((beneficiary: BeneficiariesModel, index: number) => (
				<div key={`beneficiary-${beneficiary.id || 'new'}-${index}`}>
					{isLiteMode
						? <>
							<Row gutter={[16, 16]}>
								<NameField
									name={ beneficiary.name }
									translations={ translations}
									errors={ errors }
									index={ index }
									size='s'
									handleChangeData={ handleChangeData }
									disabled={ isReadOnly }
								/>
								<LastNameField
									lastName={ beneficiary.lastName }
									translations={ translations }
									errors={ errors }
									index={ index }
									size='s'
									handleChangeData={ handleChangeData }
									disabled={ isReadOnly }
								/>
								<SecondLastNameField
									lastName={ beneficiary.secondLastName! }
									translations={ translations }
									errors={ errors }
									index={ index }
									size='s'
									handleChangeData={ handleChangeData }
									required={ beneficiary.nationality === 'Portugal' }
									disabled={ isReadOnly }
								/>
								<CountryField
									value={ beneficiary.nationality }
									fieldName='nationality'
									nationalitiesList={ nationalitiesList }
									translations={ translations }
									errors={ errors }
									index={ index }
									handleSelect={ handleSelectData }
									language={ language }
									contractLanguage={ contractLanguage }
									disabled={ isReadOnly }
								/>
								<Col xs={ 24 } md={ 6 } lg={ 6 } className="titles">
									<label className="required"> { translations.porcentajeParticipacion }</label>
									<Input
										type="number"
										name="participationPercentage"
										suffix="%"
										value={ beneficiary.participationPercentage }
										disabled={ isReadOnly }
										onChange={ handleChangeData(index) }
										status={ errors[index].participationPercentage || errors[index].percentageLesserThan25 ? 'error' : '' }
									/>
									<span className={ `error ${errors[index].participationPercentage ? 'show' : null }` }>{translations.completeCampo }</span>
									<span className={ `error ${errors[index].percentageLesserThan25 ? 'show' : null }` }>{translations.percentageGreaterThan5 || translations.percentageLesserThan25 }</span>
								</Col>
							</Row>
							<Row gutter={[16, 16]} justify="end" style={{ width: '100%' }}>
								<Col style={{ textAlign: 'right' }}>
									<Popconfirm
										title={ translations.eliminarBeneficiario }
										okText={ translations.si }
										cancelText={ translations.no }
										onConfirm={ () => deleteRow(index) }
										disabled={ isReadOnly }
									>
										<Button danger className="dlt-btn-benef" disabled={ isReadOnly }>{ translations.delete }</Button>
									</Popconfirm>
								</Col>
							</Row>
							<Row>
								<Divider />
							</Row>
						</>
						: <>
							<Row gutter={[16, 16]}>
								<NameField
									name={ beneficiary.name }
									translations={ translations}
									errors={ errors }
									index={ index }
									size='s'
									handleChangeData={ handleChangeData }
									disabled={ isReadOnly }
								/>
								<LastNameField
									lastName={ beneficiary.lastName }
									translations={ translations }
									errors={ errors }
									index={ index }
									size='s'
									handleChangeData={ handleChangeData }
									disabled={ isReadOnly }
								/>
								<SecondLastNameField
									lastName={ beneficiary.secondLastName! }
									translations={ translations }
									errors={ errors }
									index={ index }
									size='s'
									handleChangeData={ handleChangeData }
									required={ beneficiary.nationality === 'Portugal' }
									disabled={ isReadOnly }
								/>
								<DateField
									value={ beneficiary.birthDate }
									fieldName="birthDate"
									required
									translations={ translations }
									errors={ errors }
									index={ index }
									handleDatePicker={ handleChangeDate }
									disabled={ isReadOnly }
								/>
							</Row>
							<Row gutter={[16, 16]}>
								<EmailField
									value={ beneficiary.email }
									fieldName='email'
									translations={ translations }
									errors={ errors }
									index={ index }
									handleChangeData={ handleChangeData }
									disabled={ isReadOnly }
								/>
								<DocumentTypeField
									value={ beneficiary.documentType! }
									index={ index }
									translations={ translations }
									errors={ errors }
									handleSelect={ handleSelectData }
									disabled={ isReadOnly }
								/>
								<Col xs={24} md={6} lg={6} className="titles">
									<label className="required"> { translations.identificacionFiscalBeneficiario }</label>
									<Input
										type="text"
										name="nif"
										value={ beneficiary.nif }
										disabled={ isReadOnly }
										onChange={ handleChangeData(index) }
										status={ errors[index].nif || errors[index].notValidDocument ? 'error' : '' }
									/>
									<span className={ `error ${errors[index].nif ? 'show' : null }` }>{translations.completeCampo }</span>
									<span className={ `error ${errors[index].notValidDocument ? 'show' : null }` }>{ translations.invalidIdDocument }</span>
								</Col>
								<DocumentExpirationField
									documentExpiration={ beneficiary.documentExpiration }
									translations={ translations }
									errors={ errors }
									fieldName='documentExpiration'
									index={ index }
									handleDatePicker={ handleChangeDate }
									disabled={ isReadOnly }
								/>
								<CountryField
									value={ beneficiary.nationality }
									fieldName='nationality'
									nationalitiesList={ nationalitiesList }
									translations={ translations }
									errors={ errors }
									index={ index }
									handleSelect={ handleSelectData }
									language={ language }
									contractLanguage={ contractLanguage }
									disabled={ isReadOnly }
								/>
								<FundsSourceField
									value={ beneficiary.fundsSource }
									fieldName='fundsSource'
									index={ index }
									translations={ {...translations, sourceOptions: physicalSourceOptions } }
									errors={ errors }
									handleSelectOrDatePicker={ handleSelectData }
									disabled={ isReadOnly }
								/>
								{ beneficiary.fundsSource === "Otro"
									? <TextField
										size='m'
										value={ beneficiary.otherFundsSource }
										fieldName="otherFundsSource"
										placeholder={ translations.otherFundsSource }
										translations={ translations }
										required={ false }
										errors={ errors }
										index={ index }
										handleChange={ handleChangeData }
										disabled={ isReadOnly }
									/>
									: null
								}
							</Row>
							<Row gutter={[16, 16]} justify="start" style={{ width: "102%" }}>
								<Col xs={24} md={12} lg={12}>
									<label className="required"> {translations.taxResidence} </label>
									<Select
										style={{ width: "100%" }}
										showSearch
										value={taxAddressInputs[index]}
										placeholder={translations.direccion}
										disabled={isReadOnly}
										onSearch={handleSearchAddress(index, 'taxResidence')}
										onChange={handleSelectAddress(index, 'taxResidence')}
										filterOption={false}
										notFoundContent={isPlacePredictionsLoading ? "Loading..." : null}
									>
										{activeAddress && activeAddress.index === index && activeAddress.field === "taxResidence"
											? placePredictions.map((item) => (
												<Select.Option key={item.place_id} value={item.description}>
													{item.description}
												</Select.Option>
											))
											: null
										}
									</Select>
									<span className={`error ${errors[index].taxResidence ? 'show' : null}`}>{translations.completeCampo}</span>
								</Col>
							<TextField
								size='m'
								value={beneficiary.taxResidenceExtra}
									fieldName="taxResidenceExtra"
									placeholder={translations.taxResidenceExtra}
									translations={translations}
									required={false}
									index={index}
									errors={errors}
								handleChange={handleChangeData}
								disabled={isReadOnly}
							/>
						</Row>
						{manualAddressVisibility[index]?.taxResidence && (
							<Row gutter={[16, 16]} justify="start" style={{ width: "102%" }}>
								<TextField
									size='m'
									label={translations.direccion}
									value={beneficiary.taxResidence}
									fieldName="taxResidence"
									errorWhenEmpty
									translations={translations}
									required
									errors={errors}
									index={index}
									handleChange={handleChangeData}
									disabled={isReadOnly}
								/>
								<TextField
									size='s'
									value={beneficiary.taxResidencePostalCode}
									fieldName="taxResidencePostalCode"
									translations={translations}
									required
									errors={errors}
									index={index}
									handleChange={handleChangeData}
									disabled={isReadOnly}
								/>
								<TextField
									size='s'
									value={beneficiary.taxResidenceCity}
									fieldName="taxResidenceCity"
									translations={translations}
									required
									errors={errors}
									index={index}
									handleChange={handleChangeData}
									disabled={isReadOnly}
								/>
								<CountryField
									value={beneficiary.countryTaxResidence}
									fieldName="countryTaxResidence"
									nationalitiesList={nationalitiesList}
									translations={translations}
									errors={errors}
									index={index}
									handleSelect={handleSelectData}
									language={language}
									contractLanguage={contractLanguage}
									disabled={isReadOnly}
								/>
							</Row>
						)}
						<Row gutter={[16, 16]}>
								<RoleField
									value={ beneficiary.role }
									fieldName='role'
									index={ index }
									contractLanguage={ contractLanguage }
									translations={ translations }
									errors={ errors }
									handleSelect={ handleSelectData }
									language={ language }
									disabled={ isReadOnly }
								/>
								<Col xs={ 24 } md={ 6 } lg={ 6 } className="titles">
									<label className="required"> {translations.isBeneficialOwner }</label>
									<div>
										<Switch
											checked={beneficiary.isBeneficialOwner}
											checkedChildren={<CheckOutlined />}
											unCheckedChildren={<CloseOutlined />}
											disabled={isReadOnly}
											onChange={onChangeIsBeneficialOwner(index)}
										/>
									</div>
								</Col>
								{
									beneficiary.isBeneficialOwner
										? <Col xs={ 24 } md={ 6 } lg={ 6 } className="titles">
											<label className="required"> { translations.porcentajeParticipacion }</label>
											<Input
												type="number"
												name="participationPercentage"
												suffix="%"
												value={ beneficiary.participationPercentage }
												disabled={ isReadOnly }
												onChange={ handleChangeData(index) }
												status={ errors[index].participationPercentage || errors[index].percentageLesserThan25 ? 'error' : '' }
											/>
											<span className={ `error ${errors[index].participationPercentage ? 'show' : null }` }>{translations.completeCampo }</span>
											<span className={ `error ${errors[index].percentageLesserThan25 ? 'show' : null }` }>{translations.percentageLesserThan25 }</span>
										</Col>
										: null
								}
								<Col xs={ 24 } md={ 12 } lg={ 12 } className="titles">
									<label className="required"> {translations.isPRP } <span className="tooltip-benef" onClick={ getInfo }>2  </span></label>
									<div>
										<Switch
											checked={beneficiary.hasPRP}
											checkedChildren={<CheckOutlined />}
											unCheckedChildren={<CloseOutlined />}
											disabled={isReadOnly}
											onChange={onChangeHasPRP(index)}
										/>
									</div>
								</Col>
								{beneficiary.hasPRP
									? <Col xs={ 24 } md={ 12 } lg={ 12 } className="titles">
										<label className="required"> {translations.prpDetails } </label>
										<Input
											type="text"
											name="prp"
											value={ beneficiary.prp }
											disabled={ isReadOnly }
											onChange={ handleChangeData(index) }
											status={ errors[index].prp ? 'error' : '' }
										/>
										<span className={ `error ${errors[index].prp ? 'show' : null }` }>{translations.completeCampo }</span>
									</Col>
									: null
								}
							</Row>
							<Row gutter={[16, 16]} justify="end" style={{ width: '100%' }}>
								<Col style={{ textAlign: 'right' }}>
									<Popconfirm
										title={ translations.eliminarBeneficiario }
										okText={ translations.si }
										cancelText={ translations.no }
										onConfirm={ () => deleteRow(index) }
										disabled={ isReadOnly }
									>
										<Button danger className="dlt-btn-benef" disabled={ isReadOnly }>{ translations.delete }</Button>
									</Popconfirm>
								</Col>
							</Row>
							<Row>
								<Divider />
							</Row>
						</>
					}
				</div>
			))}
		</FormLayout>
	);
};

export default Beneficiaries;
